import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDiscordAuth } from '../../hooks/useDiscordAuth';
import Logo from '../../components/Logo';
import './AuthCallback.css';

/*
  /auth/callback
  Discord redirects here after the user authorises.
  Picks up ?code= from the URL, calls the Netlify function,
  then shows success/error and redirects home.
*/
export default function AuthCallback() {
  const { handleCallback } = useDiscordAuth();
  const navigate           = useNavigate();
  const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'error'
  const [userData, setUserData] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const ranRef = useRef(false);

  useEffect(() => {
    // Guard against double-invoke in React StrictMode
    if (ranRef.current) return;
    ranRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const code   = params.get('code');
    const err    = params.get('error');

    if (err || !code) {
      setStatus('error');
      setErrorMsg(err === 'access_denied'
        ? 'You cancelled the login.'
        : 'No authorisation code received from Discord.');
      return;
    }

    handleCallback(code)
      .then(({ user, joined }) => {
        setUserData({ ...user, joined });
        setStatus('success');
        // Redirect home after 3 seconds
        setTimeout(() => navigate('/home'), 3000);
      })
      .catch((e) => {
        setStatus('error');
        setErrorMsg(e.message || 'Something went wrong. Please try again.');
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="auth-callback page">
      <div className="auth-callback__card">
        <Logo size={48} />

        {status === 'loading' && (
          <>
            <div className="auth-callback__spinner" aria-label="Loading" />
            <h2>Signing you in…</h2>
            <p>Talking to Discord, hang tight.</p>
          </>
        )}

        {status === 'success' && userData && (
          <>
            <div className="auth-callback__avatar-wrap">
              <img
                src={userData.avatar}
                alt={userData.username}
                className="auth-callback__avatar"
              />
              <span className="auth-callback__avatar-tick">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                  <polyline points="2 6 5 9 10 3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
            </div>
            <h2>Welcome, {userData.username}!</h2>
            {userData.joined
              ? <p className="auth-callback__joined">You have been added to the PERM Systems Discord server.</p>
              : <p className="auth-callback__joined">You are already a member of the PERM Systems server.</p>
            }
            <p className="auth-callback__redirect">Redirecting you home in a moment…</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="auth-callback__error-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            <h2>Something went wrong</h2>
            <p>{errorMsg}</p>
            <pre className="auth-callback__debug">{errorMsg}</pre>
            <button className="btn btn--primary" onClick={() => navigate('/home')}>
              Go home
            </button>
          </>
        )}
      </div>
    </div>
  );
}
